import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

type Variant = 'ghost' | 'solid' | 'active' | 'danger';
type Size = 'sm' | 'md';

type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /** The icon element, typically a lucide-react icon. */
  children: ReactNode;
  /** Accessible label — required since the button has no visible text. */
  label: string;
  variant?: Variant;
  size?: Size;
};

const base =
  'inline-flex items-center justify-center rounded-full ' +
  'transition-colors duration-pop ease-out-strong ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60 ' +
  'disabled:opacity-40 disabled:pointer-events-none';

const variants: Record<Variant, string> = {
  ghost: 'text-fg-2 hover:text-white hover:bg-white/[0.08]',
  solid: 'bg-white/[0.06] text-fg-2 hover:bg-white/[0.10] hover:text-white',
  active: 'bg-emerald-400/15 text-emerald-200 ring-1 ring-emerald-400/30',
  danger: 'text-rose-300/80 hover:text-rose-200 hover:bg-rose-500/15',
};

const sizes: Record<Size, string> = {
  sm: 'h-7 w-7 [&_svg]:h-4 [&_svg]:w-4',
  md: 'h-9 w-9 [&_svg]:h-[18px] [&_svg]:w-[18px]',
};

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { children, label, variant = 'ghost', size = 'md', className, type, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-label={label}
      title={rest.title ?? label}
      className={cn(base, variants[variant], sizes[size], className)}
      {...rest}
    >
      {children}
    </button>
  );
});

export default IconButton;
